import ClockIcon from '@atlaskit/icon/core/clock';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import GoalIcon from '@atlaskit/icon/core/goal';
import HashtagIcon from '@atlaskit/icon/core/hashtag';
import HomeIcon from '@atlaskit/icon/core/home';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import ProjectIcon from '@atlaskit/icon/core/project';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { useIntl } from 'react-intl-next';

import {
  DASHBOARDS_PATH,
  GOAL_DIRECTORY_PATH,
  HOME_PATH,
  PEOPLE_DIRECTORY_SEARCH_PATH,
  PROJECT_DIRECTORY_PATH,
  PROJECT_FEED_PATH,
  RECENTS_PAGE,
  TAGS_PATH,
} from '@townsquare/config/routes';
import { dashboardDirectoryRoute, dashboardRoute } from '@townsquare/dashboards-view/routes';
import { goalViewRoute } from '@townsquare/goal-view/route';
import { goalDirectoryRoute } from '@townsquare/goals-directory-view/route';
import {
  goalUpdateViewRoute,
  projectUpdateViewRoute,
  recentsViewRoute,
  savedViewsUpdatesRoute,
  tagFeedViewRoute,
  teamUpdatesViewRoute,
} from '@townsquare/home-view/routes';
import { homeTabRoute } from '@townsquare/hometab-view';
import { projectRoute } from '@townsquare/project-view/route';
import { projectDirectoryRoute } from '@townsquare/projects-directory-view/route';
import { kudosProfileRoute, profileRoute, teamProfileRoute } from '@townsquare/ptc-directory-view';
import { generatePath, useRouteDependencies } from '@townsquare/router/primitives';
import { staffDirectoryRoute } from '@townsquare/staff-directory-view/route';
import { useFeatureGate } from '@townsquare/stat-sig/gate';
import { tagsDirectoryRoute } from '@townsquare/tags-directory-view/route';
import { topicRoute } from '@townsquare/topic-view/route';
import { useWorkspaceType } from '@townsquare/workspace-store/hooks';

export const useSideNavItems = () => {
  const intl = useIntl();
  const workspaceType = useWorkspaceType();
  const isDashboardsEnabled = useFeatureGate('atlas_enable_dashboard_route') && workspaceType === 'GLOBAL_EXPERIENCE';
  const routeDependencies = useRouteDependencies();

  return [
    {
      key: HOME_PATH,
      path: generatePath(homeTabRoute, routeDependencies),
      matches: [homeTabRoute],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.your-home',
        defaultMessage: 'Your home',
        description: 'Side nav button to open home page',
      }),
      testId: 'side-nav-home',
      Icon: HomeIcon,
    },
    {
      key: RECENTS_PAGE,
      path: generatePath(recentsViewRoute, routeDependencies),
      matches: [recentsViewRoute],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.recents',
        description: 'Side nav button to open recents page',
        defaultMessage: 'Recent',
      }),
      testId: 'side-nav-recents',
      Icon: ClockIcon,
    },
    {
      key: PROJECT_FEED_PATH,
      path: generatePath(projectUpdateViewRoute, routeDependencies),
      matches: [
        projectUpdateViewRoute,
        goalUpdateViewRoute,
        tagFeedViewRoute,
        savedViewsUpdatesRoute,
        teamUpdatesViewRoute,
      ],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.status-updates',
        defaultMessage: 'Status updates',
        description: 'Side nav button to open projects updates page',
      }),
      testId: 'side-nav-status-updates',
      Icon: MegaphoneIcon,
    },
    {
      key: GOAL_DIRECTORY_PATH,
      path: generatePath(goalDirectoryRoute, routeDependencies),
      matches: [goalDirectoryRoute, goalViewRoute],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.goals',
        description: 'Side nav button to open goals page',
        defaultMessage: 'Goals',
      }),
      testId: 'side-nav-goals',
      Icon: GoalIcon,
    },
    {
      key: PROJECT_DIRECTORY_PATH,
      path: generatePath(projectDirectoryRoute, routeDependencies),
      matches: [projectDirectoryRoute, projectRoute],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.projects',
        description: 'Side nav button to open projects page',
        defaultMessage: 'Projects',
      }),
      testId: 'side-nav-projects',
      Icon: ProjectIcon,
    },
    {
      key: PEOPLE_DIRECTORY_SEARCH_PATH,
      path: generatePath(staffDirectoryRoute, routeDependencies),
      matches: [staffDirectoryRoute, profileRoute, kudosProfileRoute, teamProfileRoute],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.teams',
        description: 'Side nav button to open teams search page',
        defaultMessage: 'Teams',
      }),
      testId: 'side-nav-teams',
      Icon: TeamsIcon,
    },
    {
      key: TAGS_PATH,
      path: generatePath(tagsDirectoryRoute, routeDependencies),
      matches: [tagsDirectoryRoute, topicRoute],
      name: intl.formatMessage({
        id: 'townsquare.atlassian-home.side-nav.tags',
        description: 'Side nav button to open topics tags',
        defaultMessage: 'Tags',
      }),
      testId: 'side-nav-tags',
      Icon: HashtagIcon,
    },
    ...(isDashboardsEnabled
      ? [
          {
            key: DASHBOARDS_PATH,
            path: generatePath(dashboardDirectoryRoute, routeDependencies),
            matches: [dashboardDirectoryRoute, dashboardRoute],
            name: intl.formatMessage({
              id: 'townsquare.atlassian-home.side-nav.dashboards',
              description: 'Side nav button to open dashboards tags',
              defaultMessage: 'Dashboards',
            }),
            testId: 'side-nav-dashboards',
            Icon: DashboardIcon,
          },
        ]
      : []),
  ];
};
